import type { CaseResponseDto, TravelerResponseDto } from '@bandido/shared/dtos';
import type { HotelReservationHolder, HotelReservationRoom } from '@bandido/shared/types';
import {
  hotelBookingTravelers,
  hotelTravelerSlots,
  travelerAgeAt,
  type HotelTravelerSlot,
} from './hotelReservationFlow';
import type { HotelSearchDraft } from './searchModel';

/** Transient form data only: no accepted quote, attempt ID or booking state survives a new Search. */
export interface HotelSearchSelection {
  caseData: CaseResponseDto;
  holder: HotelReservationHolder;
  holderTravelerId: string | null;
  travelerIds: string[];
  rooms: HotelReservationRoom[];
  checkIn: string;
  checkOut: string;
}

export function hotelTravelerAgeIssue(
  slot: HotelTravelerSlot,
  person: TravelerResponseDto | undefined,
  checkIn: string,
) {
  if (!person) return null;
  const birth = person.birthDate?.slice(0, 10);
  if (!birth) return slot.childAge === null ? null : { key: 'travelerBirthRequired' };
  const age = travelerAgeAt(birth, checkIn);
  if (age === null) return { key: 'travelerBirthInvalid' };
  if (slot.childAge === null && age < 18) return { key: 'travelerNotAdult', age };
  if (slot.childAge !== null && age !== slot.childAge) return { key: 'travelerAgeMismatch', age, quoted: slot.childAge };
  return null;
}

/** Keep room membership; regroup adults/children only when an age crosses the boundary. */
export function hotelCorrectedSearch(
  draft: HotelSearchDraft,
  rooms: HotelReservationRoom[],
  ids: string[],
  roster: TravelerResponseDto[],
): { draft: HotelSearchDraft; rooms: HotelReservationRoom[]; travelerIds: string[] } | null {
  const slots = hotelTravelerSlots(rooms);
  if (rooms.length !== draft.rooms.length || ids.length !== slots.length || new Set(ids).size !== ids.length) return null;
  const correctedRooms: HotelReservationRoom[] = [];
  const travelerIds: string[] = [];
  for (const [roomIndex, room] of rooms.entries()) {
    const members: { id: string; age: number }[] = [];
    for (const [index, slot] of slots.entries()) {
      if (slot.roomIndex !== room.room_index) continue;
      const person = roster.find((person) => person.id === ids[index]);
      if (!person?.firstName.trim() || !person.lastName.trim()) return null;
      const birth = person.birthDate?.slice(0, 10);
      const age = birth ? travelerAgeAt(birth, draft.checkIn) : slot.childAge === null ? 30 : null;
      if (age === null || age > 120) return null;
      members.push({ id: person.id, age });
    }
    const adults = members.filter((person) => person.age >= 18);
    const children = members.filter((person) => person.age < 18);
    if (!adults.length) return null;
    correctedRooms.push({
      ...room,
      room_index: roomIndex,
      adults: adults.length,
      children_ages: children.map((person) => person.age),
    });
    travelerIds.push(...[...adults, ...children].map((person) => person.id));
  }
  if (!hotelBookingTravelers(correctedRooms, travelerIds, roster, draft.checkIn)) return null;
  return {
    draft: {
      ...draft,
      rooms: correctedRooms.map((room) => ({ paxes: [...Array<number>(room.adults).fill(30), ...room.children_ages] })),
    },
    rooms: correctedRooms,
    travelerIds,
  };
}

/** A new fare may return rooms/child slots in a different order; match within the same room. */
export function hotelRestoredTravelerIds(
  selection: HotelSearchSelection | undefined,
  rooms: HotelReservationRoom[],
  checkIn?: string,
  checkOut?: string,
): string[] {
  if (
    !selection ||
    selection.checkIn !== checkIn ||
    selection.checkOut !== checkOut ||
    selection.rooms.length !== rooms.length
  ) return [];
  const previousSlots = hotelTravelerSlots(selection.rooms);
  if (previousSlots.length !== selection.travelerIds.length) return [];
  const available = previousSlots.map((slot, index) => ({ ...slot, id: selection.travelerIds[index], used: false }));
  const ids: string[] = [];
  for (const [index, room] of rooms.entries()) {
    const previous = selection.rooms[index];
    if (room.adults !== previous.adults || room.children_ages.length !== previous.children_ages.length) return [];
    for (const slot of hotelTravelerSlots([room])) {
      const match = available.find(
        (person) => !person.used && person.roomIndex === previous.room_index && person.childAge === slot.childAge,
      );
      if (!match) return [];
      match.used = true;
      ids.push(match.id);
    }
  }
  return ids;
}
